import type { Dayjs } from 'dayjs'

import { dayjs } from './dayjs'

export type DateRangeTuple = [Dayjs, Dayjs]
export type DateRangeUnit = 'day' | 'month' | 'week'
export type StringDateRange = [string, string]
export type DateRangeDisabledDate = (current: Dayjs) => boolean

export interface DateRangePreset {
  label: string
  value: DateRangeTuple
}

interface RecentDaysOptions {
  includeToday?: boolean
  fullDay?: boolean
}

interface RecentPeriodOptions {
  includeCurrent?: boolean
  fullPeriod?: boolean
}

interface DisableDateRangeOptions {
  max?: Dayjs
  min: Dayjs
  unit?: DateRangeUnit
}

type DatePresetDefinition =
  | { days: number; kind: 'range'; label: string }
  | { kind: 'single'; label: string; offset: number }

const DATE_PRESET_DEFINITIONS: DatePresetDefinition[] = [
  { kind: 'single', label: '今天', offset: 0 },
  { kind: 'single', label: '昨天', offset: 1 },
  { kind: 'single', label: '前天', offset: 2 },
  { days: 3, kind: 'range', label: '近三天' },
  { days: 7, kind: 'range', label: '近一周' },
  { days: 30, kind: 'range', label: '近一月' },
]

const MAX_PRESET_SEARCH_DAYS = 366

function isDateRangeAllowed(
  start: Dayjs,
  days: number,
  disabledDate: DateRangeDisabledDate,
) {
  return Array.from({ length: days }).every(
    (_, index) => !disabledDate(start.add(index, 'day')),
  )
}

function findLatestAllowedRange(
  days: number,
  disabledDate: DateRangeDisabledDate,
): DateRangeTuple | undefined {
  const current = dayjs()

  for (let offset = 0; offset <= MAX_PRESET_SEARCH_DAYS; offset += 1) {
    const end = current.subtract(offset, 'day')
    const start = end.subtract(days - 1, 'day')

    if (isDateRangeAllowed(start, days, disabledDate)) {
      return [start, end]
    }
  }

  return undefined
}

export const DateRanges = {
  /**
   * 获取今天的范围。
   * `fullDay` 为 `true` 时，返回今天整天的开始和结束时间。
   * `fullDay` 为 `false` 时，开始和结束都使用当前时刻。
   */
  today(fullDay = false): DateRangeTuple {
    const current = dayjs()
    return fullDay
      ? [current.startOf('day'), current.endOf('day')]
      : [current, current]
  },

  /**
   * 获取昨天的范围。
   * `fullDay` 为 `true` 时，返回昨天整天的开始和结束时间。
   * `fullDay` 为 `false` 时，开始和结束都使用“昨天此刻”。
   */
  yesterday(fullDay = false): DateRangeTuple {
    const current = dayjs().subtract(1, 'day')
    return fullDay
      ? [current.startOf('day'), current.endOf('day')]
      : [current, current]
  },

  /**
   * 获取最近 N 天的范围。
   * 默认包含今天，例如 `lastDays(7)` 表示最近 7 天且包含今天。
   * `fullDay` 为 `true` 时，会补齐到开始日 00:00:00 和结束日 23:59:59。
   */
  lastDays(days: number, options: RecentDaysOptions = {}): DateRangeTuple {
    const { includeToday = true, fullDay = false } = options
    const end = includeToday ? dayjs() : dayjs().subtract(1, 'day')
    const start = end.subtract(Math.max(days - 1, 0), 'day')

    return fullDay ? [start.startOf('day'), end.endOf('day')] : [start, end]
  },

  /**
   * 获取日期范围选择器的常用快捷项。
   * 默认会过滤或回退到不命中 `disabledDate` 的日期范围。
   */
  recentDayPresets(
    disabledDate?: DateRangeDisabledDate,
    respectDisabledDate = true,
  ): DateRangePreset[] {
    return DATE_PRESET_DEFINITIONS.flatMap((definition) => {
      if (definition.kind === 'single') {
        const value = dayjs().subtract(definition.offset, 'day')

        if (respectDisabledDate && disabledDate?.(value)) {
          return []
        }

        return [{ label: definition.label, value: [value, value] }]
      }

      const value =
        respectDisabledDate && disabledDate
          ? findLatestAllowedRange(definition.days, disabledDate)
          : DateRanges.lastDays(definition.days)

      return value ? [{ label: definition.label, value }] : []
    })
  },

  /**
   * 获取最近 N 周的范围。
   * 默认包含本周。
   * `fullPeriod` 为 `true` 时返回完整自然周范围，
   * 否则返回每周起始时间，适合周选择器默认值。
   */
  lastWeeks(weeks: number, options: RecentPeriodOptions = {}): DateRangeTuple {
    const { includeCurrent = true, fullPeriod = false } = options
    const end = includeCurrent ? dayjs() : dayjs().subtract(1, 'week')
    const start = end.subtract(Math.max(weeks - 1, 0), 'week')

    return fullPeriod
      ? [start.startOf('week'), end.endOf('week')]
      : [start.startOf('week'), end.startOf('week')]
  },

  /**
   * 获取最近 N 个月的范围。
   * 默认包含本月。
   * `fullPeriod` 为 `true` 时返回完整自然月范围，
   * 否则返回每月起始时间，适合月选择器默认值。
   */
  lastMonths(
    months: number,
    options: RecentPeriodOptions = {},
  ): DateRangeTuple {
    const { includeCurrent = true, fullPeriod = false } = options
    const end = includeCurrent ? dayjs() : dayjs().subtract(1, 'month')
    const start = end.subtract(Math.max(months - 1, 0), 'month')

    return fullPeriod
      ? [start.startOf('month'), end.endOf('month')]
      : [start.startOf('month'), end.startOf('month')]
  },

  /**
   * 获取最近 N 个已经结束的自然周。
   * 不包含本周，适合周报“默认截止到上周”的场景。
   */
  lastCompleteWeeks(weeks: number): DateRangeTuple {
    return this.lastWeeks(weeks, {
      includeCurrent: false,
      fullPeriod: false,
    })
  },

  /**
   * 把一个周范围补齐成完整自然周。
   * 返回“开始周周初”到“结束周周末”的范围。
   */
  fullWeek(dateRange?: DateRangeTuple | null) {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return undefined
    }

    return [
      dateRange[0].startOf('week'),
      dateRange[1].endOf('week'),
    ] as DateRangeTuple
  },

  /**
   * 把时间范围转换成 `['YYYY-MM-DD', 'YYYY-MM-DD']`。
   * 如果传入的已经是字符串范围，则原样返回。
   * 如果范围为空，则返回 `undefined`。
   */
  dates(
    dateRange?: DateRangeTuple | null | string[],
  ): StringDateRange | undefined {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      return undefined
    }

    if (typeof dateRange[0] === 'string' && typeof dateRange[1] === 'string') {
      return [dateRange[0], dateRange[1]]
    }

    return [
      dayjs(dateRange[0] as Dayjs).format('YYYY-MM-DD'),
      dayjs(dateRange[1] as Dayjs).format('YYYY-MM-DD'),
    ]
  },

  /**
   * 把时间范围转换成 `['YYYY-MM-DD', 'YYYY-MM-DD'] | null`。
   * 适合接口参数要求“有值传数组，没值传 null”的场景，
   * 可以直接替换页面或 API 里常见的三元表达式。
   */
  datesOrNull(dateRange?: DateRangeTuple | null | string[]) {
    return this.dates(dateRange) ?? null
  },

  /**
   * 禁用今天之后的日期。
   * 适合 `DatePicker` / `RangePicker` 的 `disabledDate`。
   */
  disableFuture(current?: Dayjs | null) {
    return current ? current.isAfter(dayjs(), 'day') : false
  },

  /** 禁用今天及未来日期。 */
  disableTodayAndFuture(current?: Dayjs | null) {
    return current ? !current.isBefore(dayjs(), 'day') : false
  },

  /**
   * 禁用指定范围之外的日期。
   * `unit` 用来控制比较粒度，可以按天、周、月判断。
   */
  disableOutside(
    current: Dayjs | null | undefined,
    options: DisableDateRangeOptions,
  ) {
    if (!current) {
      return false
    }

    const { max = dayjs(), min, unit = 'day' } = options
    return current.isBefore(min, unit) || current.isAfter(max, unit)
  },
}
